import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { money, useShop } from "../context/ShopContext";

export default function Cart() {
  const { cart, setQuantity, removeLine, subtotal, deliveryFee, total, hasSubscription, mixedBasket } = useShop();

  if (cart.length === 0) {
    return (
      <div className="min-h-[75vh] grid place-items-center bg-cream px-6" data-testid="cart-empty">
        <div className="text-center max-w-md">
          <ShoppingBag size={34} strokeWidth={1.2} className="text-gold mx-auto mb-7" />
          <h1 className="font-serif text-4xl text-ink">Your bag is empty</h1>
          <p className="text-ink-muted mt-4 leading-relaxed">Have a look through the range — the car kit is where most people start.</p>
          <Link to="/shop" data-testid="cart-empty-shop-link"
            className="inline-flex items-center gap-3 mt-9 px-9 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors">
            Browse the shop <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-[80vh]" data-testid="cart-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">Your bag</h1>
        <span className="block w-14 h-px bg-gold mt-5" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-12">
          <div className="lg:col-span-2 border-t border-cream-border">
            {cart.map((l, i) => (
              <div key={`${l.product_id}-${i}`} className="flex gap-5 py-7 border-b border-cream-border" data-testid={`cart-line-${i}`}>
                <Link to={`/shop/${l.slug}`} className="w-24 h-24 shrink-0 overflow-hidden bg-cream-alt">
                  <img src={l.image} alt={l.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Link to={`/shop/${l.slug}`} className="font-serif text-lg text-ink hover:text-gold transition-colors">{l.name}</Link>
                      {l.billing_interval && (
                        <span className="ml-3 text-[0.6rem] uppercase tracking-[0.18em] text-gold border border-gold px-2 py-0.5" data-testid={`cart-sub-badge-${i}`}>
                          Billed monthly
                        </span>
                      )}
                      {l.scents?.length > 0 && (
                        <p className="text-xs text-ink-muted mt-1 leading-relaxed" data-testid={`cart-line-scents-${i}`}>
                          {l.scents.join(" · ")}
                        </p>
                      )}
                    </div>
                    <button onClick={() => removeLine(i)} className="text-ink-muted hover:text-red-500 transition-colors p-1" data-testid={`cart-remove-${i}`} aria-label="Remove">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-5">
                    <div className="flex items-center border border-cream-border">
                      <button onClick={() => setQuantity(i, l.quantity - 1)} className="w-9 h-9 grid place-items-center text-ink hover:text-gold" data-testid={`cart-minus-${i}`}><Minus size={13} /></button>
                      <span className="w-9 text-center text-sm text-ink" data-testid={`cart-qty-${i}`}>{l.quantity}</span>
                      <button onClick={() => setQuantity(i, l.quantity + 1)} className="w-9 h-9 grid place-items-center text-ink hover:text-gold" data-testid={`cart-plus-${i}`}><Plus size={13} /></button>
                    </div>
                    <span className="font-serif text-lg text-ink" data-testid={`cart-line-total-${i}`}>{money(l.price * l.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="border border-cream-border bg-[#fbf8f1] p-8 h-max" data-testid="cart-summary">
            <h2 className="font-serif text-2xl text-ink">Summary</h2>
            <div className="mt-7 space-y-3 text-sm">
              <div className="flex justify-between text-ink-muted"><span>Subtotal</span><span className="text-ink" data-testid="cart-subtotal">{money(subtotal)}</span></div>
              <div className="flex justify-between text-ink-muted"><span>Delivery</span><span className="text-ink" data-testid="cart-delivery">{deliveryFee === 0 ? "Free" : money(deliveryFee)}</span></div>
            </div>
            <div className="flex justify-between items-baseline border-t border-cream-border mt-6 pt-6">
              <span className="text-[0.65rem] uppercase tracking-[0.18em] text-ink-muted">{hasSubscription ? "Per month" : "Total"}</span>
              <span className="font-serif text-3xl text-ink" data-testid="cart-total">{money(total)}</span>
            </div>
            {mixedBasket ? (
              <p className="mt-7 border border-gold bg-[#f4eee2] p-4 text-xs text-ink leading-relaxed" data-testid="cart-mixed-warning">
                Refill plans have to be checked out on their own. Please remove either the plan or
                the one-off items to continue.
              </p>
            ) : (
              <Link to="/checkout" data-testid="cart-checkout-button"
                className="w-full mt-8 inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors">
                Checkout <ArrowRight size={15} />
              </Link>
            )}
            <Link to="/shop" className="block text-center text-xs uppercase tracking-[0.18em] text-ink-muted mt-5 hover:text-gold transition-colors">
              Keep shopping
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}
